const BASE = import.meta.env.VITE_API_URL ?? "";

let _token: string | null = null;
export function setAuthToken(t: string | null) { _token = t; }

let _onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) { _onUnauthorized = fn; }

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string>),
  };
  if (_token) headers["Authorization"] = `Bearer ${_token}`;

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  // API Gateway returns 403 for authorizer failures (incl. expired JWTs) and
  // 401 for missing tokens. Treat both as unauthorized — don't try to read
  // a body (often empty or HTML) which would bubble up as a raw JSON parse
  // failure the user can't interpret.
  if (res.status === 401 || res.status === 403) {
    _onUnauthorized?.();
    throw new ApiError(res.status, "Your session has expired — please log in again.");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText || `HTTP ${res.status}` }));
    throw new ApiError(res.status, body.error ?? res.statusText ?? `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;

  // 2xx with a non-JSON body shouldn't happen, but if it does (API Gateway
  // integration misconfig, Lambda returned raw text, etc.) surface a readable
  // error instead of the raw SyntaxError from res.json().
  try {
    return await res.json() as T;
  } catch {
    throw new ApiError(res.status, "Server returned an invalid response — try again.");
  }
}

export const api = {
  get:    <T>(path: string) => apiFetch<T>(path, { method: "GET" }),
  post:   <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: "POST",   body: JSON.stringify(body) }),
  put:    <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: "PUT",    body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: "DELETE" }),
};
